/**
 * @file Shared closed-state resolution helpers for parent-board records.
 */

import { appData, appDataDetails } from '../../app/state/state.js';
import {
    getBoardRegistryEntries,
    getBoardRegistryEntryByItemType,
} from '../board/boardRegistry.js';

function getStatusConfigForItemType(itemType) {
    const config = appData?.config || {};
    const statusConfigKey = getBoardRegistryEntryByItemType(itemType)
        ?.fields?.statusConfigKey;

    return statusConfigKey && Array.isArray(config[statusConfigKey]) ?
        config[statusConfigKey] :
        [];
}

function getStatusValueForItemType(itemType, item) {
    if (!item) {return null;}
    const statusField = getBoardRegistryEntryByItemType(itemType)
        ?.fields?.statusField || "status";
    return item[statusField] || null;
}

function getItemTypeFromCachedState(id) {
    if (!id) {return null;}
    for (const board of getBoardRegistryEntries()) {
        if (appData?.[board.collectionName]?.some(i => i.id === id)) {
            return board.itemType;
        }
    }
    if (appDataDetails?.tasks?.some(i => i.id === id)) {return 'task';}
    if (appData?.users?.some(i => i.id === id)) {return 'user';}
    return null;
}

/**
 * Resolves closed state from item type and status config.
 * @param {string | null} itemType Parent-board item type.
 * @param {string | null} statusId Stored status ID.
 * @returns {boolean} True when the status is configured as closed.
 */
export function resolveItemClosedFromStatus(itemType, statusId) {
    if (!itemType || !statusId) {return false;}
    const statusList = getStatusConfigForItemType(itemType);
    const statusConfig = statusList.find((entry) => entry.id === statusId);
    return statusConfig?.isClosed === true;
}

/**
 * Resolves an item's closed state from cached state or status config.
 * @param {object | null} item Item record.
 * @param {string | null} [itemTypeOverride=null] Optional item type override.
 * @returns {boolean} True when the item is closed.
 */
export function getResolvedItemClosedValue(item, itemTypeOverride = null) {
    if (!item) {return false;}
    if (typeof item.isClosedResolved === 'boolean') {
        return item.isClosedResolved;
    }

    const itemType = itemTypeOverride || getItemTypeFromCachedState(item.id);
    if (!itemType) {return false;}

    const statusId = getStatusValueForItemType(itemType, item);
    return resolveItemClosedFromStatus(itemType, statusId);
}

/**
 * Resolves Asset issue-log closed state from issue status config.
 * @param {string | null} statusId Stored issue status ID.
 * @returns {boolean} True when the issue status is closed.
 */
export function resolveIssueClosedFromStatus(statusId) {
    if (!statusId) {return false;}
    const issueStatuses = appData?.config?.issueStatus || [];
    const statusConfig = issueStatuses.find((entry) => entry.id === statusId);
    return statusConfig?.isClosed === true;
}

/**
 * Resolves an Asset issue-log entry's closed state.
 * @param {object | null} issue Issue-log entry.
 * @returns {boolean} True when the issue entry is closed.
 */
export function getResolvedIssueClosedValue(issue) {
    if (!issue) {return false;}
    if (typeof issue.isClosedResolved === 'boolean') {
        return issue.isClosedResolved;
    }

    return resolveIssueClosedFromStatus(issue.status);
}

/**
 * Checks if an item (patient, project, asset, task) is considered "closed"
 * based on its resolved closed-state helper field or direct status config.
 * @param {object | null} item - The item object (must have an `id` and a status property).
 * @returns {boolean} True if the item is considered closed, false otherwise or if item/config invalid.
 */
export function isItemClosed(item) {
    if (!item || !item.id) {return false;}
    return getResolvedItemClosedValue(item);
}
